import React, { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { Container, Row, Col, Card, Table, Button, Form } from 'react-bootstrap';
import reportIcon from '../assets/icons/report.svg';
import { useAuth } from '../hooks/useAuth';

/**
 * Sales tax collected on the month's payments, in dollars.
 *
 * Prefers `tax_collected_cash` (the tax portion of cash actually received).
 * Falls back to the accrual `tax_collected` only when the backend predates
 * migration_52 -- that figure repeats a rental's whole tax in every month it
 * was paid, so it understates gross profit on multi-month leases.
 */
export function storeTaxCollected(store) {
  return parseFloat(store?.tax_collected_cash ?? store?.tax_collected ?? 0) || 0;
}

/**
 * Gross profit = pre-tax revenue less car costs, in dollars.
 *
 * The revenue fields are cash and tax-inclusive (payments settle
 * amount + tax_amount), so tax is backed out here: it is a pass-through
 * liability owed to the state, never margin. Returns null when fee_revenue is
 * absent, since an incomplete sum would read as a real number.
 */
export function storeGrossProfit(store) {
  if (store?.fee_revenue == null) return null;
  return (
    (parseFloat(store.rental_revenue || 0) || 0) +
    (parseFloat(store.rto_revenue || 0) || 0) +
    (parseFloat(store.fee_revenue) || 0) -
    (parseFloat(store.repair_costs || 0) || 0) -
    storeTaxCollected(store)
  );
}

/** Commission rates billed on each revenue stream. See contract/README.md. */
export const IT_FEE_RENTAL_RATE = 0.07;
export const IT_FEE_RTO_RATE = 0.03;

/**
 * IT fee billed for the month, in dollars: 7% of rental revenue plus 3% of RTO
 * revenue, per store.
 *
 * This mirrors the commission half of the monthly invoice produced by
 * contract/generate_invoice.py from this same endpoint. The invoice also
 * prorates shared costs (Google Ads, Twilio) across stores, but those are
 * hand-entered per month and are not available here -- so this column is the
 * commission only, and will read slightly lower than the invoice total.
 */
export function storeItFee(store) {
  return (
    (parseFloat(store?.rental_revenue || 0) || 0) * IT_FEE_RENTAL_RATE +
    (parseFloat(store?.rto_revenue || 0) || 0) * IT_FEE_RTO_RATE
  );
}

/**
 * Reports component displays admin-only analytics and store comparison
 * Completely redesigned for Task 03: Location-Based Monthly Analysis
 */
export function Reports() {
  const { apiCall } = useOutletContext();
  const { user } = useAuth();
  // Date selection state
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  
  // Data state
  const [storesComparison, setStoresComparison] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [vehiclePerformance, setVehiclePerformance] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStoresComparison();
  }, [selectedYear, selectedMonth]);

  // Auto-load vehicle performance when only one store is available (manager view)
  useEffect(() => {
    if (storesComparison.length === 1) {
      loadVehiclePerformance(storesComparison[0].store_id);
    }
  }, [storesComparison]);

  const loadStoresComparison = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('tegrat_auth_token');
      const response = await fetch(
        `${API_BASE_URL}/analytics/stores/comparison?year=${selectedYear}&month=${selectedMonth}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load stores comparison');
      }

      const data = await response.json();
      setStoresComparison(data.stores || []);
    } catch (err) {
      console.error('Error loading stores comparison:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const loadVehiclePerformance = async (storeId) => {
    try {
      const token = localStorage.getItem('tegrat_auth_token');
      const response = await fetch(
        `${API_BASE_URL}/analytics/store/${storeId}/vehicles?year=${selectedYear}&month=${selectedMonth}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load vehicle performance');
      }

      const data = await response.json();
      setVehiclePerformance(data);
      setSelectedStore(storeId);
    } catch (err) {
      console.error('Error loading vehicle performance:', err);
    }
  };

  const handleRefreshCache = async () => {
    setRefreshing(true);
    
    try {
      const token = localStorage.getItem('tegrat_auth_token');
      const response = await fetch(
        `${API_BASE_URL}/analytics/refresh`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            year: selectedYear,
            month: selectedMonth,
            // Non-admin users must include store_id
            ...(user?.role !== 'admin' && storesComparison.length > 0 && {
              store_id: storesComparison[0].store_id,
            }),
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to refresh cache');
      }

      // Reload data after refresh
      await loadStoresComparison();
      if (selectedStore) {
        await loadVehiclePerformance(selectedStore);
      }
    } catch (err) {
      console.error('Error refreshing cache:', err);
      setError('Failed to refresh cache');
    } finally {
      setRefreshing(false);
    }
  };

  // Generate year options (last 3 years)
  const yearOptions = [];
    for (let i = 2025; i <= now.getFullYear(); i++) {
        yearOptions.push(i);
    }

  // Month names
    const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  if (loading && storesComparison.length === 0) {
    return (
      <Container fluid className="py-4">
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading analytics data...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-4 align-items-center">
        <Col md={6}>
          <h2>
            <img src={reportIcon} alt="" style={{ width: 28, height: 28, marginRight: 10, verticalAlign: 'middle', display: 'inline-block', filter: 'none' }} />
            Location-Based Monthly Analysis
          </h2>
        </Col>
        <Col md={6} className="text-end">
          <Button 
            variant="primary" 
            onClick={handleRefreshCache}
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing...' : 'Refresh Cache'}
          </Button>
        </Col>
      </Row>

      {/* Month/Year Selector */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={2}>
              <Form.Label>Year</Form.Label>
              <Form.Select 
                value={selectedYear} 
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              >
                {yearOptions.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label>Month</Form.Label>
              <Form.Select 
                value={selectedMonth} 
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              >
                {monthNames.map((name, index) => (
                  <option key={index + 1} value={index + 1}>{name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={7}>
              <div className="mt-4">
                <small className="text-muted">
                  Viewing data for <strong>{monthNames[selectedMonth - 1]} {selectedYear}</strong>
                </small>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && (
        <div className="alert alert-danger">{error}</div>
      )}

      {/* Stores Comparison Table */}
      <Card className="mb-4">
        <Card.Header>
          <h3>{storesComparison.length === 1 ? 'Store Monthly Summary' : 'All Stores Comparison'}</h3>
        </Card.Header>
        <Card.Body className="p-0">
          {storesComparison.length > 0 ? (
            <Table striped hover responsive>
              <thead>
                <tr>
                  <th>Store</th>
                  <th>Rental Revenue</th>
                  <th>RTO Revenue</th>
                  <th>Car Costs</th>
                  <th title="Sales tax included in the payments received this month">Tax Collected</th>
                  <th title="Rental + RTO + fee revenue, less car costs and sales tax">Gross Profit</th>
                  <th title="7% of rental revenue + 3% of RTO revenue. Commission only -- the invoice also prorates shared costs across stores.">IT Fee</th>
                  <th>Rentals</th>
                  <th>Active Vehicles</th>
                  <th>Idle Vehicles</th>
                  {storesComparison.length > 1 && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {storesComparison.map(store => {
                  return (
                    <tr key={store.store_id}>
                      <td>
                        <strong>{store.store_name}</strong>
                        <br />
                        <small className="text-muted">{store.city}, {store.state}</small>
                      </td>
                      <td style={{ color: '#27ae60', fontWeight: 'bold' }}>
                        ${parseFloat(store.rental_revenue || 0).toFixed(2)}
                      </td>
                      <td style={{ color: '#2980b9', fontWeight: 'bold' }}>
                        ${parseFloat(store.rto_revenue || 0).toFixed(2)}
                      </td>
                      <td style={{ color: '#e74c3c', fontWeight: 'bold' }}>
                        ${parseFloat(store.repair_costs || 0).toFixed(2)}
                      </td>
                      <td style={{ color: '#e67e22', fontWeight: 'bold' }}>
                        ${storeTaxCollected(store).toFixed(2)}
                      </td>
                      <td style={{ color: '#16a085', fontWeight: 'bold' }}>
                        {storeGrossProfit(store) == null ? 'N/A' : `$${storeGrossProfit(store).toFixed(2)}`}
                      </td>
                      <td style={{ color: '#8e44ad', fontWeight: 'bold' }}>
                        ${storeItFee(store).toFixed(2)}
                      </td>
                      <td>{store.rental_count || 0}</td>
                      <td>{store.active_vehicles || 0}</td>
                      <td>{store.idle_vehicles || 0}</td>
                      {storesComparison.length > 1 && (
                        <td>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => loadVehiclePerformance(store.store_id)}
                          >
                            View Details
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {storesComparison.length > 0 && (() => {
                  const totalRental = storesComparison.reduce((sum, s) => sum + parseFloat(s.rental_revenue || 0), 0);
                  const totalRto = storesComparison.reduce((sum, s) => sum + parseFloat(s.rto_revenue || 0), 0);
                  const feeAvailable = storesComparison.every(s => s.fee_revenue != null);
                  const totalFee = feeAvailable ? storesComparison.reduce((sum, s) => sum + parseFloat(s.fee_revenue), 0) : null;
                  const totalCosts = storesComparison.reduce((sum, s) => sum + parseFloat(s.repair_costs || 0), 0);
                  const totalTax = storesComparison.reduce((sum, s) => sum + storeTaxCollected(s), 0);
                  const totalGrossProfit = feeAvailable ? totalRental + totalRto + totalFee - totalCosts - totalTax : null;
                  const totalItFee = storesComparison.reduce((sum, s) => sum + storeItFee(s), 0);
                  return (
                    <tr style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa', borderTop: '2px solid #dee2e6' }}>
                      <td><strong>Sum</strong></td>
                      <td style={{ color: '#27ae60', fontWeight: 'bold' }}>${totalRental.toFixed(2)}</td>
                      <td style={{ color: '#2980b9', fontWeight: 'bold' }}>${totalRto.toFixed(2)}</td>
                      <td style={{ color: '#e74c3c', fontWeight: 'bold' }}>${totalCosts.toFixed(2)}</td>
                      <td style={{ color: '#e67e22', fontWeight: 'bold' }}>${totalTax.toFixed(2)}</td>
                      <td style={{ color: '#16a085', fontWeight: 'bold' }}>{totalGrossProfit == null ? 'N/A' : `$${totalGrossProfit.toFixed(2)}`}</td>
                      <td style={{ color: '#8e44ad', fontWeight: 'bold' }}>${totalItFee.toFixed(2)}</td>
                      <td colSpan={storesComparison.length > 1 ? 4 : 3}></td>
                    </tr>
                  );
                })()}
              </tbody>
            </Table>
          ) : (
            <div className="p-3 text-muted text-center">No data available for selected period</div>
          )}
        </Card.Body>
      </Card>

      {/* Vehicle Performance Breakdown (when store selected) */}
      {selectedStore && vehiclePerformance.vehicles && vehiclePerformance.vehicles.length > 0 && (
        <Card>
          <Card.Header>
            <Row className="align-items-center">
              <Col>
                <h3>Vehicle Performance - {vehiclePerformance.store_name || `Store ${selectedStore}`}</h3>
              </Col>
              <Col className="text-end">
                <Button 
                  size="sm" 
                  variant="outline-secondary"
                  onClick={() => {
                    setSelectedStore(null);
                    setVehiclePerformance({});
                  }}
                >
                  Close
                </Button>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="p-0">
            <Table striped hover responsive>
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>License Plate</th>
                  <th>Rental Revenue</th>
                  <th>RTO Revenue</th>
                  <th>Car Costs</th>
                  <th>Rentals</th>
                  <th>Days Rented</th>
                  <th>Days Available</th>
                  <th>Utilization</th>
                </tr>
              </thead>
              <tbody>
                {vehiclePerformance.vehicles.map(vehicle => (
                  <tr key={vehicle.vehicle_id}>
                    <td>
                      <Link to={`/dashboard/vehicles/${vehicle.vehicle_id}`} style={{ textDecoration: 'none' }}>
                        {vehicle.year} {vehicle.make} {vehicle.model}
                      </Link>
                    </td>
                    <td>{vehicle.license_plate}</td>
                    <td style={{ color: '#27ae60' }}>
                      ${parseFloat(vehicle.rental_revenue || 0).toFixed(2)}
                    </td>
                    <td style={{ color: '#2980b9' }}>
                      ${parseFloat(vehicle.rto_revenue || 0).toFixed(2)}
                    </td>
                    <td style={{ color: '#e74c3c', fontWeight: 'bold' }}>
                      ${parseFloat(vehicle.repair_costs || 0).toFixed(2)}
                    </td>
                    <td>{vehicle.rental_count || 0}</td>
                    <td>{vehicle.days_rented || 0}</td>
                    <td>{vehicle.days_available || 0}</td>
                    <td>
                      <span style={{
                        color: vehicle.utilization_rate >= 70 ? '#27ae60' :
                               vehicle.utilization_rate >= 40 ? '#f39c12' : '#e74c3c'
                      }}>
                        {parseFloat(vehicle.utilization_rate || 0).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}

export default Reports;
